/**
 * ConfigurationProcessor.js
 * Utility class for processing customer display configuration data
 */

class ConfigurationProcessor {
    static CONFIG_MAPPINGS = {
        // FTP settings
        customerdisplayftphost: { target: 'ftp', property: 'host' },
        customerdisplayftpuser: { target: 'ftp', property: 'username' },
        customerdisplayftppass: { target: 'ftp', property: 'password' },
        customerdisplayftppath: { target: 'ftp', property: 'path' },
        customerdisplayftpport: { target: 'ftp', property: 'port' },
        customerdisplayurl: { target: 'ftp', property: 'url' },
        
        // Simple values
        customerdisplaydefaultduration: { target: 'defaultDuration' },
        customerdisplayheadertext: { target: 'headerText' },
        
        // Split screen (special handling for groep)
        customerdisplaysplitsalesscreen: { target: 'splitScreen' },
        customerdisplaysamesettingsallstores: { target: 'sameSettings' },
        
        // Color mappings (support both general and store-specific)
        customerdisplayheadercolor: { target: 'colors', property: 'headerBackgroundColor' },
        customerdisplayheadertextcolor: { target: 'colors', property: 'headerTextColor' },
        customerdisplayheadercustomercolor: { target: 'colors', property: 'customerHeaderBackgroundColor' },
        customerdisplayheadercustomertextcolor: { target: 'colors', property: 'customerHeaderTextColor' },
        customerdisplaysalesrowcolor: { target: 'colors', property: 'itemRowBackgroundColor' },
        customerdisplaysalesrowtextcolor: { target: 'colors', property: 'itemTextColor' },
        customerdisplayfootercolor: { target: 'colors', property: 'totalSectionBackgroundColor' },
        customerdisplayfootertextcolor: { target: 'colors', property: 'totalTextColor' },
        customerdisplayfootercardcolor: { target: 'colors', property: 'reductionSectionBackgroundColor' }, // not yet used (has kasvar)
        customerdisplayfootercardtextcolor: { target: 'colors', property: 'reductionSectionTextColor' }, // not yet used (has kasvar)
        customerdisplaymainbackgroundcolor: { target: 'colors', property: 'mainBackgroundColor' }, // not yet used (no kasvar)
        customerdisplayfootertextcolor2: { target: 'colors', property: 'totalSubTextColor' },
        customerdisplaysofttouchcolor: { target: 'colors', property: 'sftFooterBackgroundColor' },
        customerdisplaysofttouchtextcolor: { target: 'colors', property: 'sftFooterTextColor' }
    };

    static DEFAULT_COLORS = {
        mainBackgroundColor: '#ffffff',
        headerBackgroundColor: '#f8f9fa',
        customerHeaderBackgroundColor: '#f8f9fa',
        itemRowBackgroundColor: '#ffffff',
        totalSectionBackgroundColor: '#e9ecef',
        sftFooterBackgroundColor: '#000000',
        headerTextColor: '#000000',
        customerHeaderTextColor: '#333333',
        itemTextColor: '#000000',
        totalTextColor: '#000000',
        totalSubTextColor: '#000000',
        sftFooterTextColor: '#ffffff',
        primaryAccentColor: '#007bff',
        secondaryAccentColor: '#28a745',
        highlightColor: '#ffc107',
        borderColor: '#dee2e6',
        separatorColor: '#ced4da'
    };

    /**
     * Extract store ID from configuration code
     * @param {string} code - Configuration code
     * @returns {string|null} Store ID or null
     */
    static getStoreId = (code) => code?.slice(-2);

    /**
     * Check if value represents boolean true
     * @param {string} value - Value to check
     * @returns {boolean} True if value equals "T"
     */
    static isBooleanTrue = (value) => value === "T";

    /**
     * Process configuration data and return organized results
     * @param {Array} data - Array of configuration objects from API
     * @param {Object} setters - Object containing setter functions for special cases
     * @param {Function} setters.setCustomerDisplayHeaderText - Setter for header text
     * @param {Function} setters.setGeneralStoreImageSplit - Setter for split screen on general store
     * @param {Function} setters.setSameSettingsAllStores - Setter for same settings flag
     * @returns {Object} Processed configuration data
     */
    static processConfiguration(data, setters = {}) {
        // Initialize result object
        const result = {
            ftp: { host: "", port: "", username: "", password: "", url: "" },
            defaultDuration: 0,
            generalColors: { ...this.DEFAULT_COLORS },
            storeScreenTypes: [],
            storeColors: [],
            headerText: ""
        };

        // Maps for efficient store-specific data building
        const storeColorsMap = new Map();
        const storeScreenTypesMap = new Map();

        // Process each configuration item
        data.forEach(item => {
            const { var: varName, value, groep, code } = item;
            const varKey = varName.toLowerCase();
            const mapping = this.CONFIG_MAPPINGS[varKey];

            if (!mapping) {
                if (varKey === 'customerdisplayv2') {
                    // Handle customer display version 2 enablement if needed
                    console.log('Customer Display V2 setting detected:', value);
                }
                return;
            }

            this.processConfigItem(item, mapping, result, storeColorsMap, storeScreenTypesMap, setters);
        });

        // Convert Maps to Arrays for React state
        result.storeScreenTypes = Array.from(storeScreenTypesMap.entries())
            .map(([id, isSplitScreen]) => ({ id, isSplitScreen }));

        result.storeColors = Array.from(storeColorsMap.entries())
            .map(([id, colors]) => ({ id, ...colors }));

        return result;
    }

    /**
     * Process individual configuration item
     * @param {Object} item - Configuration item from API
     * @param {Object} mapping - Mapping configuration for this item
     * @param {Object} result - Result object to populate
     * @param {Map} storeColorsMap - Map for store-specific colors
     * @param {Map} storeScreenTypesMap - Map for store-specific screen types
     * @param {Object} setters - Setter functions object
     */
    static processConfigItem(item, mapping, result, storeColorsMap, storeScreenTypesMap, setters) {
        const { value, groep, code } = item;

        switch (mapping.target) {
            case 'ftp':
                result.ftp[mapping.property] = value;
                break;

            case 'defaultDuration':
                result.defaultDuration = value;
                break;

            case 'headerText':
                result.headerText = value;
                // Call setter if provided
                if (setters.setCustomerDisplayHeaderText) {
                    setters.setCustomerDisplayHeaderText(value);
                }
                break;

            case 'splitScreen':
                if (groep == "2") {
                    const storeId = this.getStoreId(code);
                    if (storeId) {
                        storeScreenTypesMap.set(storeId, this.isBooleanTrue(value));
                    }
                } else if (groep == "1") {
                    // Call setter if provided
                    if (setters.setGeneralStoreImageSplit) {
                        setters.setGeneralStoreImageSplit(this.isBooleanTrue(value));
                    }
                }
                break;
            case 'sameSettings':
                if(setters.setSameSettingsAllStores) {
                    setters.setSameSettingsAllStores(this.isBooleanTrue(value));
                }
            case 'colors':
                if (groep == "1") {
                    // General color setting
                    result.generalColors[mapping.property] = value;
                } else if (groep == "2") {
                    // Store-specific color setting
                    const storeId = this.getStoreId(code);
                    if (storeId) {
                        if (!storeColorsMap.has(storeId)) {
                            storeColorsMap.set(storeId, {});
                        }
                        storeColorsMap.get(storeId)[mapping.property] = value;
                    }
                }
                break;

            default:
                console.warn(`Unknown target type: ${mapping.target}`);
                break;
        }
    }

    /**
     * Get the list of configuration codes that this processor can handle
     * @returns {Array<string>} Array of configuration codes
     */
    static getSupportedConfigCodes() {
        return [
            'CustomerDisplayV2', 'CustomerDisplayFTPHost', 'CustomerDisplayFTPUser', 
            'CustomerDisplayFTPPass', 'CustomerDisplayFTPPath', 'CustomerDisplayFTPPort', 'CustomerDisplayURL', 
            'CustomerDisplayDefaultDuration', 'CustomerDisplaySplitSalesScreen', 
            'CustomerDisplayGeneralColors', 'CustomerDisplayStoreColors',
            'CustomerDisplayHeadercolor', 'CustomerDisplayHeaderTextcolor', 
            'CustomerDisplayHeaderCustomercolor', 'CustomerDisplayHeaderCustomerTextcolor', 
            'CustomerDisplaySoftTouchcolor', 'CustomerDisplaySoftTouchTextcolor', 
            'CustomerDisplayFootercolor', 'CustomerDisplayFooterTextcolor',
            'CustomerDisplayFooterCardcolor', 'CustomerDisplayFooterCardTextcolor', 
            'CustomerDisplayFooterTextcolor2', 'CustomerDisplaySalesRowColor', 
            'CustomerDisplaySalesRowTextColor', 'CustomerDisplayHeaderText', 
            'CustomerDisplaySameSettingsAllStores'
        ];
    }
}

export default ConfigurationProcessor;